import * as React from "react";
import { SignUpFormSlotsProvider } from "@grandvisionhq/auth";
import { CancelAndReturnsModalContentSlotsProvider, CancelAndReturnsModalContent, PreferredStoreFormSlotsProvider, PersonalDetailsFormSlotsProvider, StoreAddressFormSlotsProvider, AddressFormSlotsProvider, PaymentDetailsFormSlotsProvider, CCCAddressFormSlotsProvider, AppointmentSummarySlotsProvider } from "@grandvisionhq/my-account";
import { FormGroup, FirstName, LastName, Street, HouseNumber, HouseNumberSuffix, City, PostalCode, Region, FormControl, Util, PhoneNumberInput, DateOfBirth, Email } from "@grandvisionhq/forms";
import { useIntl } from "@grandvisionhq/state";
import { Card, MediaQuery } from "@grandvisionhq/common";
import { Label } from "@grandvisionhq/elements";
import { useLink } from "@grandvisionhq/frk-routing";
const DefaultAddressForm = () => {
  const { getLabel } = useIntl();
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FormGroup, { columns: 2 }, /* @__PURE__ */ React.createElement(
    FirstName,
    {
      label: getLabel("checkout.contact.firstName"),
      errorMessage: getLabel("checkout.contact.firstName.errorMessage")
    }
  ), /* @__PURE__ */ React.createElement(
    LastName,
    {
      label: getLabel("checkout.contact.lastName"),
      errorMessage: getLabel("checkout.contact.lastName.errorMessage")
    }
  )), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
    Street,
    {
      label: getLabel("checkout.address.street"),
      errorMessage: getLabel("checkout.address.street.errorMessage"),
      maxLength: 50
    }
  )), /* @__PURE__ */ React.createElement(FormGroup, { columns: 2 }, /* @__PURE__ */ React.createElement(
    HouseNumber,
    {
      label: getLabel("checkout.address.streetNumber"),
      errorMessage: getLabel("checkout.address.streetNumber.errorMessage")
    }
  ), /* @__PURE__ */ React.createElement(
    HouseNumberSuffix,
    {
      required: false,
      label: getLabel("checkout.address.streetNumber.suffix"),
      optionalText: getLabel("checkout.form.field.optional")
    }
  )), /* @__PURE__ */ React.createElement(FormGroup, { columns: 2 }, /* @__PURE__ */ React.createElement(
    City,
    {
      label: getLabel("checkout.address.city"),
      errorMessage: getLabel("checkout.address.city.errorMessage")
    }
  ), /* @__PURE__ */ React.createElement(
    PostalCode,
    {
      label: getLabel("checkout.address.postalCode"),
      errorMessage: getLabel("checkout.address.postalCode.errorMessage")
    }
  )), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
    Region,
    {
      label: getLabel("checkout.address.district"),
      errorMessage: getLabel("checkout.address.district.errorMessage")
    }
  )));
};
function concatenateTruthyValues(values, delimiter = " ") {
  return values.filter(Boolean).join(delimiter);
}
const concat = concatenateTruthyValues;
function concatenateAddress$1(values) {
  const firstLine = concat([values.postalCode, values.city]);
  return concat([firstLine, values.region], ", ");
}
const DefaultAddressSummary = ({ actions, values = {} }) => {
  const { getLabel } = useIntl();
  return /* @__PURE__ */ React.createElement(Card, null, /* @__PURE__ */ React.createElement(Card.Content, null, actions?.onEdit && /* @__PURE__ */ React.createElement(Card.Actions, null, /* @__PURE__ */ React.createElement(Card.Button, { onClick: actions.onEdit, children: getLabel("common.actions.edit") })), /* @__PURE__ */ React.createElement(Card.Text, null, values.firstName, " ", values.lastName), /* @__PURE__ */ React.createElement(Card.Text, null, concat([values.streetName, values.streetNumber, values.additionalStreetInfo])), /* @__PURE__ */ React.createElement(Card.Text, null, concatenateAddress$1(values))));
};
const DefaultAppointmentSummary = ({ className, appointment }) => {
  const { appointmentType, startTime, invitee, store, isReservation, isVirtualAppointment, child } = appointment;
  const { dateTime, getLabel } = useIntl();
  const durationString = appointmentType?.duration ? `(${appointmentType.duration.amount} ${getLabel(
    `appointments.shortTimeUnits.${appointmentType.duration.unit.toLowerCase()}`
  )})` : "";
  const { href, onClick } = useLink({
    route: "me.appointments.detail",
    params: { appointmentId: appointment.id }
  }) ?? {};
  return /* @__PURE__ */ React.createElement(Card, { className }, /* @__PURE__ */ React.createElement(Card.Content, null, /* @__PURE__ */ React.createElement(Card.Section, { flex: true, flexContent: true }, invitee && /* @__PURE__ */ React.createElement(Card.Text, { variant: "body-head" }, /* @__PURE__ */ React.createElement(React.Fragment, null, invitee.firstName, " ", invitee.lastName)), isReservation && /* @__PURE__ */ React.createElement(Label, { type: "neutral", variant: "small" }, getLabel("appointments.reservation.label"))), /* @__PURE__ */ React.createElement(Card.Section, { spacing: "s" }, child && /* @__PURE__ */ React.createElement(Card.Text, { variant: "body-head" }, /* @__PURE__ */ React.createElement(React.Fragment, null, getLabel("appointments.component.appointmentOverview.child"), ": ", child.firstName, " ", child.lastName))), /* @__PURE__ */ React.createElement(Card.Section, null, appointmentType?.name && /* @__PURE__ */ React.createElement(Card.Text, { variant: "body-head" }, appointmentType.name, " ", durationString), /* @__PURE__ */ React.createElement(Card.Text, { color: "text-secondary" }, `${dateTime.formatLongDate(startTime)} - ${dateTime.formatTime(startTime)}`), !isVirtualAppointment && store && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Card.Text, { color: "text-secondary" }, store.streetName, " ", store.streetNumber), /* @__PURE__ */ React.createElement(Card.Text, { color: "text-secondary" }, store.postalCode, " ", store.town))), /* @__PURE__ */ React.createElement(Card.Section, null, /* @__PURE__ */ React.createElement(Card.Link, { onClick, href, iconAfter: "chevron-right" }, getLabel("account.appointment.detail")))));
};
const DefaultCCCAddressForm = ({ customerType }) => {
  const { getLabel, country } = useIntl();
  const postalCodeValidator = (postalCode) => Util.validations.isValidPostalCodeForCountry(country, postalCode);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, customerType === "business" ? /* @__PURE__ */ React.createElement(FormGroup, { columns: 2 }, /* @__PURE__ */ React.createElement(
    FormControl,
    {
      id: "company",
      label: getLabel("account.subscription.businessAddress.company"),
      disabled: true,
      errorMessage: getLabel("account.subscription.businessAddress.company.errorMessage")
    }
  ), /* @__PURE__ */ React.createElement(
    FormControl,
    {
      id: "ivaNumber",
      label: getLabel("account.subscription.businessAddress.ivaNumber"),
      disabled: true,
      errorMessage: getLabel("account.subscription.businessAddress.ivaNumber.errorMessage")
    }
  )) : null, /* @__PURE__ */ React.createElement(FormGroup, { columns: 2 }, /* @__PURE__ */ React.createElement(
    FirstName,
    {
      label: getLabel("checkout.contact.firstName"),
      errorMessage: getLabel("checkout.contact.firstName.errorMessage")
    }
  ), /* @__PURE__ */ React.createElement(
    LastName,
    {
      label: getLabel("checkout.contact.lastName"),
      errorMessage: getLabel("checkout.contact.lastName.errorMessage")
    }
  )), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
    FormControl,
    {
      id: "streetName",
      key: "streetName",
      "data-t": "street",
      label: getLabel("checkout.address.street"),
      errorMessage: getLabel("checkout.address.addressLine.errorMessage")
    }
  )), /* @__PURE__ */ React.createElement(FormGroup, { columns: 2 }, /* @__PURE__ */ React.createElement(
    HouseNumber,
    {
      label: getLabel("checkout.address.streetNumber"),
      errorMessage: getLabel("checkout.address.streetNumber.errorMessage")
    }
  ), /* @__PURE__ */ React.createElement(
    HouseNumberSuffix,
    {
      required: false,
      label: getLabel("checkout.address.streetNumber.suffix"),
      optionalText: getLabel("checkout.form.field.optional")
    }
  )), /* @__PURE__ */ React.createElement(FormGroup, { columns: 2 }, /* @__PURE__ */ React.createElement(
    City,
    {
      label: getLabel("checkout.address.city"),
      errorMessage: getLabel("checkout.address.city.errorMessage")
    }
  ), /* @__PURE__ */ React.createElement(
    PostalCode,
    {
      label: getLabel("checkout.address.postalCode"),
      validations: [postalCodeValidator],
      errorMessage: getLabel("checkout.address.postalCode.errorMessage")
    }
  )), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
    Region,
    {
      label: getLabel("checkout.address.district"),
      errorMessage: getLabel("checkout.address.district.errorMessage")
    }
  )));
};
const DefaultCCCAddressSummary = ({
  actions,
  className,
  values = {},
  customerType = "private",
  addressType = "deliveryAddress"
}) => {
  const { getLabel } = useIntl();
  const editLabel = getLabel("common.actions.edit");
  const title = getLabel(`account.${addressType}`);
  return /* @__PURE__ */ React.createElement(Card, { className }, /* @__PURE__ */ React.createElement(Card.Content, null, actions?.onEdit && /* @__PURE__ */ React.createElement(Card.Actions, null, /* @__PURE__ */ React.createElement(Card.Button, { onClick: actions.onEdit, children: editLabel })), /* @__PURE__ */ React.createElement(Card.Text, { variant: "body-head" }, title), /* @__PURE__ */ React.createElement(Card.Section, null, values.businessName && /* @__PURE__ */ React.createElement(Card.Text, null, values.businessName), values.ivaNumber && /* @__PURE__ */ React.createElement(Card.Text, null, "IVA: ", values.ivaNumber)), /* @__PURE__ */ React.createElement(Card.Section, null, values.firstName && /* @__PURE__ */ React.createElement(Card.Text, null, values.firstName, " ", values.lastName), values.streetName && /* @__PURE__ */ React.createElement(Card.Text, null, values.streetName, " ", values.streetinteger), values.addressLine1 && /* @__PURE__ */ React.createElement(Card.Text, null, concat([values.addressLine1, values.streetNumber, values.additionalStreetInfo])), values.addressLine2 && /* @__PURE__ */ React.createElement(Card.Text, null, values.addressLine2), values.addressLine3 && /* @__PURE__ */ React.createElement(Card.Text, null, values.addressLine3), values.region && /* @__PURE__ */ React.createElement(Card.Text, null, values.region), values.country && /* @__PURE__ */ React.createElement(Card.Text, null, values.country), (values.postalCode || values.city) && /* @__PURE__ */ React.createElement(Card.Text, null, concat([values.postalCode, values.city])))));
};
const Dl = ({ children, ...props }) => {
  const isMobile = MediaQuery.useIsPhone();
  return /* @__PURE__ */ React.createElement(
    "dl",
    {
      style: {
        display: "grid",
        gridGap: "0 2rem",
        gridTemplateColumns: isMobile ? "none" : "auto 1fr",
        marginBottom: 0
      },
      ...props
    },
    children
  );
};
const Dt = ({ children, ...props }) => /* @__PURE__ */ React.createElement(
  "dt",
  {
    style: {
      fontWeight: "normal"
    },
    ...props
  },
  children
);
const Dd = ({ children, ...props }) => {
  const isMobile = MediaQuery.useIsPhone();
  return /* @__PURE__ */ React.createElement(
    "dd",
    {
      style: {
        marginBottom: isMobile ? "1rem" : 0
      },
      ...props
    },
    children
  );
};
const DefaultPaymentDetailsSummary = ({ type, values }) => {
  const { getLabel } = useIntl();
  return /* @__PURE__ */ React.createElement(Card, null, /* @__PURE__ */ React.createElement(Card.Content, null, /* @__PURE__ */ React.createElement(Dl, null, type && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Dt, null, getLabel("account.subscription.payment.method") + ": "), /* @__PURE__ */ React.createElement(Dd, null, type)), values?.bankName && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Dt, null, getLabel("account.subscription.payment.provider") + ": "), /* @__PURE__ */ React.createElement(Dd, null, values.bankName)), values?.bankNumber && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Dt, null, getLabel("account.subscription.payment.accountNumber") + ": "), /* @__PURE__ */ React.createElement(Dd, null, values.bankNumber)), values?.bankSortCode && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Dt, null, getLabel("account.subscription.payment.sortCode") + ": "), /* @__PURE__ */ React.createElement(Dd, null, values.bankSortCode)), values?.bankAccountHolderName && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Dt, null, getLabel("account.subscription.payment.accountHolder") + ": "), /* @__PURE__ */ React.createElement(Dd, null, values.bankAccountHolderName)), values?.dueDate && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Dt, null, getLabel("account.subscription.payment.dueDate") + ": "), /* @__PURE__ */ React.createElement(Dd, null, values.dueDate)))));
};
const DefaultPersonalDetailsForm = () => {
  const { getLabel, country, locale } = useIntl();
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FormGroup, { columns: 2 }, /* @__PURE__ */ React.createElement(
    FirstName,
    {
      label: getLabel("checkout.contact.firstName"),
      errorMessage: getLabel("checkout.contact.firstName.errorMessage")
    }
  ), /* @__PURE__ */ React.createElement(
    LastName,
    {
      label: getLabel("checkout.contact.lastName"),
      errorMessage: getLabel("checkout.contact.lastName.errorMessage"),
      tooltip: getLabel("checkout.contact.lastName.tooltip")
    }
  )), /* @__PURE__ */ React.createElement(
    PhoneNumberInput,
    {
      id: "phone",
      "data-t": "phone-number",
      label: getLabel("checkout.contact.phoneNumber"),
      required: true,
      hideAsteriskSign: false,
      initialState: {
        value: ""
      },
      defaultCountry: country,
      localeForTranslations: locale,
      countriesOrder: [country],
      errorMessage: getLabel("checkout.contact.phoneNumber.errorMessage")
    }
  ), /* @__PURE__ */ React.createElement(DateOfBirth, null));
};
const DefaultPersonalDetailsSummary = ({
  actions,
  values = {},
  email
}) => {
  const { getLabel, dateTime } = useIntl();
  const LABEL_EDIT = getLabel("common.actions.edit");
  return /* @__PURE__ */ React.createElement(Card, null, /* @__PURE__ */ React.createElement(Card.Content, null, /* @__PURE__ */ React.createElement(Card.Actions, null, actions?.onEdit && /* @__PURE__ */ React.createElement(Card.Button, { onClick: actions.onEdit, children: LABEL_EDIT })), /* @__PURE__ */ React.createElement(Card.Text, { variant: "body-head" }, `${values.firstName} ${values.lastName}`), /* @__PURE__ */ React.createElement(Card.Text, null, values.phone), values.dateOfBirth && /* @__PURE__ */ React.createElement(Card.Text, null, dateTime.formatMidsizeDate(new Date(values.dateOfBirth))), /* @__PURE__ */ React.createElement(Card.Text, null, email)));
};
const DefaultPreferredStoreSummary = ({ actions, values }) => {
  const { getLabel } = useIntl();
  const LABEL_EDIT = getLabel("common.actions.edit");
  return /* @__PURE__ */ React.createElement(Card, null, /* @__PURE__ */ React.createElement(Card.Content, null, /* @__PURE__ */ React.createElement(Card.Actions, null, actions?.onEdit && /* @__PURE__ */ React.createElement(Card.Button, { onClick: actions.onEdit, children: LABEL_EDIT })), values?.storeName && /* @__PURE__ */ React.createElement(Card.Text, { variant: "body-head" }, values?.storeName), /* @__PURE__ */ React.createElement(Card.Text, null, concat([values?.streetName, values?.streetNumber])), /* @__PURE__ */ React.createElement(Card.Text, null, concat([values?.postalCode, values?.town]))));
};
const DefaultSignUpForm = () => {
  const { getLabel } = useIntl();
  return /* @__PURE__ */ React.createElement(
    Email,
    {
      label: getLabel("fields.input.email.label"),
      errorMessage: getLabel("account.error.emailInvalid"),
      required: true
    }
  );
};
function concatenateAddress(values) {
  const firstLine = concat([values.postalCode, values.city]);
  return concat([firstLine, values.region], ", ");
}
const DefaultStoreAddressSummary = ({
  actions,
  values = {},
  storeName
}) => {
  const { getLabel } = useIntl();
  return /* @__PURE__ */ React.createElement(Card, null, /* @__PURE__ */ React.createElement(Card.Content, null, actions?.onEdit && /* @__PURE__ */ React.createElement(Card.Actions, null, /* @__PURE__ */ React.createElement(Card.Button, { onClick: actions.onEdit, children: getLabel("common.actions.edit") })), storeName && /* @__PURE__ */ React.createElement(Card.Text, null, storeName), /* @__PURE__ */ React.createElement(Card.Text, null, concat([values.streetName, values.streetNumber, values.additionalStreetInfo])), /* @__PURE__ */ React.createElement(Card.Text, null, concatenateAddress(values))));
};
function MyAccountSlotsProvider({
  slots,
  children
}) {
  return /* @__PURE__ */ React.createElement(
    CancelAndReturnsModalContentSlotsProvider,
    {
      slots: {
        ModalContent: CancelAndReturnsModalContent
      }
    },
    /* @__PURE__ */ React.createElement(PreferredStoreFormSlotsProvider, { slots: { summary: slots.preferredStoreSummarySlot } }, /* @__PURE__ */ React.createElement(SignUpFormSlotsProvider, { slots: { form: slots.signUpFormSlot } }, /* @__PURE__ */ React.createElement(
      PersonalDetailsFormSlotsProvider,
      {
        slots: {
          form: slots.personalDetailsFormSlot,
          summary: slots.personalDetailsSummarySlot
        }
      },
      /* @__PURE__ */ React.createElement(
        StoreAddressFormSlotsProvider,
        {
          slots: {
            summary: slots.storeAddressSummarySlot
          }
        },
        /* @__PURE__ */ React.createElement(
          AddressFormSlotsProvider,
          {
            slots: {
              form: slots.addressFormSlot,
              summary: slots.addressSummarySlot
            }
          },
          /* @__PURE__ */ React.createElement(
            PaymentDetailsFormSlotsProvider,
            {
              slots: { summary: slots.paymentDetailsSummarySlot }
            },
            /* @__PURE__ */ React.createElement(
              CCCAddressFormSlotsProvider,
              {
                slots: {
                  form: slots.cccAddressFormSlot,
                  summary: slots.cccAddressSummarySlot
                }
              },
              /* @__PURE__ */ React.createElement(
                AppointmentSummarySlotsProvider,
                {
                  slots: { summary: slots.appointmentSummarySlot }
                },
                children
              )
            )
          )
        )
      )
    )))
  );
}
const SlotsProvider = MyAccountSlotsProvider;
const DefaultForms = {
  PersonalDetailsForm: DefaultPersonalDetailsForm,
  PersonalDetailsSummary: DefaultPersonalDetailsSummary,
  AddressForm: DefaultAddressForm,
  AddressSummary: DefaultAddressSummary,
  StoreAddressSummary: DefaultStoreAddressSummary,
  PreferredStoreSummary: DefaultPreferredStoreSummary,
  PaymentDetailsSummary: DefaultPaymentDetailsSummary,
  SignUpForm: DefaultSignUpForm,
  CCCAddressForm: DefaultCCCAddressForm,
  CCCAddressSummary: DefaultCCCAddressSummary,
  AppointmentSummary: DefaultAppointmentSummary
};
export {
  DefaultForms,
  SlotsProvider
};
//# sourceMappingURL=my-account.js.map
